import { Component, type ErrorInfo, type ReactNode } from 'react';
import { contactEmail, contactPhone, contactPhoneLabel } from '@/config/contact';

// The last line of defence against a white screen.
//
// A render error anywhere below this leaves React with no tree to show, so
// without a boundary a partner gets a blank page and nothing to click — on a site
// whose whole purpose is for them to reach us. This turns that into a page that
// says what happened and how to get hold of somebody.
//
// Only class components can catch render errors; that is the sole reason this is
// one.

interface PropsType {
  children: ReactNode;
  /**
   * Fills the viewport and draws its own branding. For the boundary that sits
   * outside the layouts, where there is no header left to sit under.
   */
  fullScreen?: boolean;
}

interface StateType {
  error: Error | null;
}

export class ErrorBoundary extends Component<PropsType, StateType> {
  state: StateType = { error: null };

  static getDerivedStateFromError(error: Error): StateType {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is all there is for now. Worth replacing with a real reporter
    // before this matters — a crash a client hits and we never hear about is the
    // failure mode this boundary makes *quieter*, not louder.
    console.error('Unhandled render error', error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return <ErrorFallback fullScreen={this.props.fullScreen} />;
  }
}

/**
 * What a client sees. No apology at length and no error text: neither helps
 * them, and the second invites them to read a stack trace to a concierge.
 *
 * Both actions are plain anchors rather than router links on purpose. A boundary
 * latches once it has caught, and a client-side navigation would re-render the
 * same broken subtree straight back into it — a full load is the only reliable
 * way out from inside the failure.
 */
function ErrorFallback({ fullScreen }: { fullScreen?: boolean }) {
  const phone = contactPhone;

  return (
    <div
      className={`tw:flex tw:flex-col tw:items-center tw:justify-center tw:px-6 tw:py-24 tw:text-center ${
        fullScreen ? 'tw:min-h-screen tw:bg-ed-cream' : ''
      }`}
    >
      {fullScreen && (
        <img src='/eightydays_balloon.png' alt='Eighty Days' className='tw:mb-8 tw:h-12 tw:w-auto tw:object-contain' />
      )}

      <h1 className='tw:font-serif tw:text-3xl tw:font-light tw:text-ed-black'>Something went wrong</h1>

      <p className='tw:mt-4 tw:max-w-md tw:text-sm tw:text-ed-charcoal/70'>
        This page didn&rsquo;t load properly. Nothing you had already sent us has been lost — and if you were
        in the middle of arranging something for a guest, please don&rsquo;t wait on it.
      </p>

      <p className='tw:mt-6 tw:max-w-md tw:text-sm tw:text-ed-charcoal/70'>
        {phone ? 'Give us a call and we will take care of it straight away.' : 'Get in touch and we will take care of it straight away.'}
      </p>

      <div className='tw:mt-4 tw:flex tw:flex-col tw:items-center tw:gap-1 tw:text-sm'>
        {/* Hidden rather than shown dead while the number is unset — see
            src/config/contact.ts. */}
        {phone && (
          <a href={`tel:${phone}`} className='tw:font-serif tw:text-2xl tw:text-ed-black tw:hover:text-ed-gold'>
            {contactPhoneLabel()}
          </a>
        )}
        <a href={`mailto:${contactEmail}`} className='tw:text-ed-umber tw:underline tw:hover:text-ed-gold'>
          {contactEmail}
        </a>
      </div>

      <div className='tw:mt-10 tw:flex tw:flex-wrap tw:items-center tw:justify-center tw:gap-3'>
        <a
          href={typeof window === 'undefined' ? '/' : window.location.pathname}
          className='tw:bg-ed-gold tw:px-6 tw:py-3 tw:text-xs tw:tracking-widest tw:text-ed-black tw:uppercase tw:transition-colors tw:hover:bg-ed-black tw:hover:text-ed-cream'
        >
          Try again
        </a>
        <a
          href='/catalog'
          className='tw:px-6 tw:py-3 tw:text-xs tw:tracking-widest tw:text-ed-umber tw:uppercase tw:underline tw:transition-colors tw:hover:text-ed-gold'
        >
          Back to experiences
        </a>
      </div>
    </div>
  );
}
